import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EventIcon from '@mui/icons-material/Event';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Stack,
    Avatar
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import updateLocale from 'dayjs/plugin/updateLocale';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(updateLocale);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.locale('es');

// Configurar dayjs para que la semana empiece en lunes
dayjs.updateLocale('es', {
    weekStart: 1,
    weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    weekdaysShort: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    weekdaysMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S']
});

const DAYS_OF_WEEK = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const FREQUENCY_OPTIONS = {
    CUSTOM: 'CUSTOM',
    CUSTOM_DAILY: 'CUSTOM_DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    BIANNUAL: 'BIANNUAL',
    ANNUAL: 'ANNUAL'
};

const FREQUENCY_LABELS = {
    CUSTOM: 'Personalizado',
    CUSTOM_DAILY: 'Personalizado por día',
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensual',
    QUARTERLY: 'Trimestral',
    BIANNUAL: 'Semestral',
    ANNUAL: 'Anual'
};

const MonthCalendar = ({ month, year, selectedRanges, onSelectRange, onRemoveRange, frequency, readOnly }) => {
    const firstDay = dayjs().year(year).month(month).startOf('month');
    const startOfWeek = firstDay.startOf('week');




    const isInAnyRange = (date) => {
        return selectedRanges.some(range =>
            date.isBetween(range.start, range.end, 'day', '[]')
        );
    };

    const getRangeIndex = (date) => {
        return selectedRanges.findIndex(range =>
            date.isBetween(range.start, range.end, 'day', '[]')
        );
    };

    const getWorkWeekRange = (date) => {
        const monday = date.startOf('week').add(0, 'day'); // Lunes
        const friday = monday.add(4, 'day'); // Viernes
        return { start: monday, end: friday };
    };

    const getDayRange = (date) => {
        // Retornar el rango del día completo (inicio y fin del día)
        const start = date.startOf('day');
        const end = date.endOf('day');
        return { start, end };
    };

    const handleDateClick = (date) => {
        const rangeIndex = getRangeIndex(date);

        if (rangeIndex !== -1) {
            // Si el día ya está en un rango, eliminar ese rango
            onRemoveRange(rangeIndex);
        } else {
            if (frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY) {
                // Si es "Personalizado por día", seleccionar solo el día completo
                const { start, end } = getDayRange(date);
                onSelectRange({ start, end });
            } else {
                // Obtener el rango de la semana laboral (Lunes a Viernes)
                const { start, end } = getWorkWeekRange(date);
                onSelectRange({ start, end });
            }
        }
    };

    const weeks = [];
    let currentDate = startOfWeek;

    while (currentDate.month() <= month || currentDate.month() === 11) {
        const week = [];
        for (let i = 0; i < 7; i++) {
            week.push(currentDate);
            currentDate = currentDate.add(1, 'day');
        }
        weeks.push(week);
        if (currentDate.month() !== month && currentDate.date() > 7) break;
    }

    return (
        <Paper
            elevation={2}
            sx={{
                aspectRatio: '1/1',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
                bgcolor: 'background.paper',
                '&:hover': {
                    boxShadow: 3
                },
                transition: 'box-shadow 0.3s ease-in-out'
            }}
        >
            <Typography
                variant="subtitle1"
                align="center"
                sx={{
                    mb: 2,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    color: 'primary.main',
                    letterSpacing: '0.5px'
                }}
            >
                {firstDay.format('MMMM')}
            </Typography>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid
                    container
                    columns={7}
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 1
                    }}
                >
                    {DAYS_OF_WEEK.map((day, index) => (
                        <Grid
                            item
                            xs={1}
                            key={`day-header-${index}`}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                pb: 1,
                                pl: '2px',
                                pr: '2px'
                            }}
                        >
                            <Typography
                                variant="caption"
                                align="center"
                                sx={{
                                    fontWeight: 500,
                                    color: index === 6 ? 'error.main' : 'text.secondary',
                                    fontSize: '0.75rem',
                                    width: '2.2rem',
                                    textAlign: 'center'
                                }}
                            >
                                {day}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {weeks.map((week, weekIndex) => (
                        <Grid container key={weekIndex} columns={7} sx={{ flex: 1 }}>
                            {week.map((date, dayIndex) => {
                                const isCurrentMonth = date.month() === month;
                                const isInRange = isInAnyRange(date);
                                const rangeIndex = getRangeIndex(date);
                                const isToday = date.isSame(dayjs(), 'day');
                                const isSunday = dayIndex === 6;
                                const isWeekend = dayIndex > 4; // Sábado o Domingo

                                return (
                                    <Grid
                                        item
                                        xs={1}
                                        key={`${weekIndex}-${dayIndex}-${date.format('YYYY-MM-DD')}`}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative',
                                            pl: '2px',
                                            pr: '2px'
                                        }}
                                    >
                                            <Box
                                                onClick={() => {
                                                    const canSelect = frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                        ? !readOnly && isCurrentMonth 
                                                        : !readOnly && isCurrentMonth && !isWeekend;
                                                    if (canSelect) handleDateClick(date);
                                                }}
                                                sx={{
                                                    width: '2.2rem',
                                                    height: '2.2rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: readOnly 
                                                        ? 'default' 
                                                        : (frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                            ? (isCurrentMonth ? 'pointer' : 'default')
                                                            : (isCurrentMonth && !isWeekend ? 'pointer' : 'default')),
                                                    backgroundColor: isInRange
                                                        ? `primary.main`
                                                        : isToday
                                                            ? 'action.selected'
                                                            : 'transparent',
                                                    color: !isCurrentMonth
                                                        ? 'text.disabled'
                                                        : isInRange
                                                            ? 'primary.contrastText'
                                                            : (frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                                ? 'text.primary'
                                                                : (isWeekend ? 'text.disabled' : 'text.primary')),
                                                    borderRadius: '50%',
                                                    transition: 'all 0.2s ease',
                                                    border: isToday ? '2px solid' : 'none',
                                                    borderColor: isToday ? 'primary.main' : 'transparent',
                                                    opacity: isCurrentMonth ? 1 : 0.3,
                                                    '&:hover': (frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                        ? (isCurrentMonth && !readOnly ? {
                                                            backgroundColor: isInRange
                                                                ? 'primary.dark'
                                                                : 'primary.light',
                                                            transform: 'scale(1.1)',
                                                        } : {})
                                                        : (!isWeekend && isCurrentMonth && !readOnly ? {
                                                            backgroundColor: isInRange
                                                                ? 'primary.dark'
                                                                : 'primary.light',
                                                            transform: 'scale(1.1)',
                                                        } : {}))
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    align="center"
                                                    sx={{
                                                        fontWeight: isInRange || isToday ? 600 : 400,
                                                        fontSize: '0.875rem',
                                                        width: '100%',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {date.date()}
                                                </Typography>
                                            </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
};

export default function CalendarRange({ 
    value, 
    valueFrecuency, 
    onApply, 
    onDeleteAll, 
    readOnly = false,
    readOnlyMessage = "Modo solo lectura"
}) {
    // Inicializar con value si existe y es un array
    const initialRanges = Array.isArray(value) ? value.map(range => ({
        start: dayjs(range.datetime_start_estimated),
        end: dayjs(range.datetime_end_estimated)
    })) : [];

    const [selectedRanges, setSelectedRanges] = useState(initialRanges);
    const [frequency, setFrequency] = useState(valueFrecuency || FREQUENCY_OPTIONS.CUSTOM);
    const currentYear = dayjs().year();

    // Actualizar cuando cambie el value
    // Memoizar la conversión de value a rangos
    // Actualizar cuando cambie el value
    useEffect(() => {
        if (Array.isArray(value)) {
            const newRanges = value.map(range => ({
                start: dayjs(range.datetime_start_estimated),
                end: dayjs(range.datetime_end_estimated)
            }));
            setSelectedRanges(newRanges);
        }
        
        if (valueFrecuency) {
            setFrequency(valueFrecuency);
        }
    }, [value, valueFrecuency]);

    // Función para el botón Aplicar
    const handleApply = () => {
        if (onApply) {
            onApply({
                dates_programmed: selectedRanges.map(range => ({
                    datetime_start_estimated: range.start.format('YYYY-MM-DD HH:mm:ss'),
                    datetime_end_estimated: range.end.format('YYYY-MM-DD HH:mm:ss')
                })),
                frequency: frequency
            });
        }
    };

    const applyFrequencyPattern = (baseRange) => {
        const patterns = {
            [FREQUENCY_OPTIONS.WEEKLY]: () => {
                // Aplicar a todas las semanas del año desde la semana seleccionada
                const ranges = [];
                let currentDate = baseRange.start;
                const endOfYear = dayjs().year(currentYear).endOf('year');

                while (currentDate.isSameOrBefore(endOfYear)) {
                    ranges.push({
                        start: currentDate.clone(),
                        end: currentDate.clone().add(4, 'day')
                    });
                    currentDate = currentDate.add(1, 'week');
                }
                return ranges;
            },
            [FREQUENCY_OPTIONS.MONTHLY]: () => {
                // Aplicar a la misma semana de cada mes desde el mes seleccionado
                const ranges = [];
                const selectedMonth = baseRange.start.month();
                const selectedWeekOfMonth = Math.ceil(baseRange.start.date() / 7);

                for (let month = selectedMonth; month < 12; month++) {
                    const monthStart = dayjs().year(currentYear).month(month).startOf('month');
                    let weekStart = monthStart;

                    // Avanzar a la semana correspondiente
                    weekStart = weekStart.add(selectedWeekOfMonth - 1, 'week');

                    // Asegurarse de que empiece en lunes
                    while (weekStart.day() !== 1) {
                        weekStart = weekStart.add(1, 'day');
                    }

                    // Verificar que la semana sigue perteneciendo al mes correcto
                    if (weekStart.month() === month) {
                        const weekEnd = weekStart.add(4, 'day');
                        ranges.push({ start: weekStart, end: weekEnd });
                    }
                }
                return ranges;
            },
            [FREQUENCY_OPTIONS.QUARTERLY]: () => {
                // Aplicar a cada trimestre desde el trimestre actual
                const ranges = [];
                const selectedMonth = baseRange.start.month();
                const startQuarter = Math.floor(selectedMonth / 3);

                // Calcular la semana relativa dentro del trimestre
                const quarterStart = dayjs().year(currentYear).month(startQuarter * 3).startOf('month');
                const weeksFromQuarterStart = Math.floor(baseRange.start.diff(quarterStart, 'weeks', true));

                for (let quarter = startQuarter; quarter < 4; quarter++) {
                    const quarterStartDate = dayjs().year(currentYear).month(quarter * 3).startOf('month');
                    let weekStart = quarterStartDate.add(weeksFromQuarterStart, 'weeks');

                    // Asegurarse de que empiece en lunes
                    while (weekStart.day() !== 1) {
                        weekStart = weekStart.add(1, 'day');
                    }

                    // Verificar que la semana está dentro del trimestre
                    const quarterEndMonth = (quarter * 3) + 2;
                    const quarterEnd = dayjs().year(currentYear).month(quarterEndMonth).endOf('month');

                    if (weekStart.isBefore(quarterEnd)) {
                        const weekEnd = weekStart.add(4, 'day');
                        ranges.push({ start: weekStart, end: weekEnd });
                    }
                }
                return ranges;
            },
            [FREQUENCY_OPTIONS.BIANNUAL]: () => {
                // Aplicar a cada semestre desde el semestre actual
                const ranges = [];
                const selectedMonth = baseRange.start.month();
                const startSemester = Math.floor(selectedMonth / 6);

                // Calcular la semana relativa dentro del semestre
                const semesterStart = dayjs().year(currentYear).month(startSemester * 6).startOf('month');
                const weeksFromSemesterStart = Math.floor(baseRange.start.diff(semesterStart, 'weeks', true));

                for (let semester = startSemester; semester < 2; semester++) {
                    const semesterStartDate = dayjs().year(currentYear).month(semester * 6).startOf('month');
                    let weekStart = semesterStartDate.add(weeksFromSemesterStart, 'weeks');

                    // Asegurarse de que empiece en lunes
                    while (weekStart.day() !== 1) {
                        weekStart = weekStart.add(1, 'day');
                    }

                    // Verificar que la semana está dentro del semestre
                    const semesterEndMonth = (semester * 6) + 5;
                    const semesterEnd = dayjs().year(currentYear).month(semesterEndMonth).endOf('month');

                    if (weekStart.isBefore(semesterEnd)) {
                        const weekEnd = weekStart.add(4, 'day');
                        ranges.push({ start: weekStart, end: weekEnd });
                    }
                }
                return ranges;
            },
            [FREQUENCY_OPTIONS.ANNUAL]: () => {
                // Usar exactamente la semana seleccionada para todo el año
                return [{
                    start: baseRange.start,
                    end: baseRange.end
                }];
            },
            [FREQUENCY_OPTIONS.CUSTOM]: () => {
                // Mantener solo el rango seleccionado
                return [baseRange];
            },
            [FREQUENCY_OPTIONS.CUSTOM_DAILY]: () => {
                // Mantener solo el rango seleccionado (día completo)
                return [baseRange];
            }
        };

        return patterns[frequency] ? patterns[frequency]() : [baseRange];
    };

    const handleSelectRange = React.useCallback((newRange) => {
        if (frequency === FREQUENCY_OPTIONS.CUSTOM || frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY) {
            setSelectedRanges(prev => [...prev, newRange]);
        } else {
            const patterns = applyFrequencyPattern(newRange);
            setSelectedRanges(patterns);
        }
    }, [frequency]);

    const handleFrequencyChange = (event) => {
        const newFrequency = event.target.value;
        setFrequency(newFrequency);
        setSelectedRanges([]); // Limpiar selecciones al cambiar la frecuencia
    };

    const handleRemoveRange = (indexToRemove) => {
        setSelectedRanges(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleReset = () => {
        setSelectedRanges([]);
        setFrequency(FREQUENCY_OPTIONS.CUSTOM);
    };

    // Función para formatear los rangos (memoizada)
    const formatRanges = useCallback((ranges) => {
        return ranges.map(range => ({
            datetime_start_estimated: range.start.format('YYYY-MM-DD HH:mm:ss'),
            datetime_end_estimated: range.end.format('YYYY-MM-DD HH:mm:ss')
        }));
    }, []);

    // Ya no necesitamos el useEffect para onChange

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                maxWidth: '1400px',
                margin: '0 auto',
                p: 0
            }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: '100%',
                        maxWidth: '600px'
                    }}
                >
                    <Stack spacing={2.5}>
                        <Typography
                            variant="subtitle1"
                            color="primary"
                            sx={{
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                textAlign: 'center'
                            }}
                        >
                            {frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                ? 'Selecciona días individuales' 
                                : 'Selecciona semanas laborales (L-V)'}
                        </Typography>
                        
                        <Stack 
                            direction="column"
                            spacing={2}
                            alignItems="stretch"
                        >
                            {readOnly ? (
                                <Box sx={{ 
                                    textAlign: 'center',
                                    py: 2,
                                    px: 3,
                                    bgcolor: 'action.hover',
                                    borderRadius: 2,
                                    color: 'text.secondary'
                                }}>
                                    <Typography variant="body2">
                                        {readOnlyMessage}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mt: 1,
                                            color: 'primary.main',
                                            fontWeight: 500
                                        }}
                                    >
                                        Frecuencia: {FREQUENCY_LABELS[frequency]}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <FormControl 
                                        size="small"
                                        fullWidth
                                    >
                                        <InputLabel>Frecuencia</InputLabel>
                                        <Select
                                            value={frequency}
                                            onChange={handleFrequencyChange}
                                            label="Frecuencia"
                                        >
                                            <MenuItem value={FREQUENCY_OPTIONS.CUSTOM}>{FREQUENCY_LABELS.CUSTOM}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.CUSTOM_DAILY}>{FREQUENCY_LABELS.CUSTOM_DAILY}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.WEEKLY}>{FREQUENCY_LABELS.WEEKLY}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.MONTHLY}>{FREQUENCY_LABELS.MONTHLY}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.QUARTERLY}>{FREQUENCY_LABELS.QUARTERLY}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.BIANNUAL}>{FREQUENCY_LABELS.BIANNUAL}</MenuItem>
                                            <MenuItem value={FREQUENCY_OPTIONS.ANNUAL}>{FREQUENCY_LABELS.ANNUAL}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Stack 
                                        direction="row" 
                                        spacing={2}
                                        justifyContent="center"
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={handleApply}
                                            size="medium"
                                            color="primary"
                                            disabled={selectedRanges.length === 0}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                px: 3,
                                                minWidth: '120px'
                                            }}
                                        >
                                            Aplicar
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleReset}
                                            size="medium"
                                            color="primary"
                                            disabled={selectedRanges.length === 0}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                px: 3,
                                                minWidth: '120px'
                                            }}
                                        >
                                            Limpiar
                                        </Button>
                                        {onDeleteAll && (
                                            <Button
                                                variant="outlined"
                                                onClick={onDeleteAll}
                                                size="medium"
                                                color="error"
                                                disabled={selectedRanges.length === 0}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 3,
                                                    minWidth: '120px'
                                                }}
                                            >
                                                Eliminar todo
                                            </Button>
                                        )}
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Paper>

                <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    {Array.from({ length: 12 }, (_, i) => (
                        <Grid item size={{ xs: 12, sm: 6 }} key={i}>
                            <MonthCalendar
                                month={i}
                                year={currentYear}
                                selectedRanges={selectedRanges}
                                onSelectRange={handleSelectRange}
                                onRemoveRange={handleRemoveRange}
                                frequency={frequency}
                                readOnly={readOnly}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Paper
                    elevation={1}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: '100%',
                        maxWidth: '800px'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="primary">
                            {frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                ? 'Días Seleccionados' 
                                : 'Semanas Seleccionadas'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Frecuencia: {FREQUENCY_LABELS[frequency]}
                        </Typography>
                    </Box>
                    {selectedRanges.length > 0 ? (
                        <Grid container spacing={1}>
                            {selectedRanges.map((range, index) => (
                                <Grid item size={{ xs: 12, sm: 6 }} key={index}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 3
                                            }
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    borderRadius: '50%',
                                                    width: 35,
                                                    height: 35,
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    backgroundColor: 'primary.main'
                                                }}
                                            >
                                                <EventIcon sx={{ fontSize: '18px' }} />
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                                                    {frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                        ? `Día ${index + 1}` 
                                                        : `Semana ${index + 1}`}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                                        ? range.start.format('DD/MM/YYYY')
                                                        : `${range.start.format('DD/MM/YYYY')} - ${range.end.format('DD/MM/YYYY')}`}
                                                </Typography>
                                            </Box>
                                            {(frequency === FREQUENCY_OPTIONS.CUSTOM || frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY) && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveRange(index)}
                                                    sx={{
                                                        '&:hover': {
                                                            bgcolor: 'error.light'
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px' }} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography color="text.secondary">
                            {frequency === FREQUENCY_OPTIONS.CUSTOM_DAILY 
                                ? 'No hay días seleccionados' 
                                : 'No hay semanas seleccionadas'}
                        </Typography>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
}